import {
  addDays,
  getMonth,
  getYear,
  isValid as isDateValid,
  parse,
  subDays,
} from 'date-fns';

// Redux
import { useAppDispatch, useAppSelector } from '../hooks/UseReduxState'
import { getOffSeasonStartDateValue, setRequestMaxDeparture, setRequestMinArrival } from '../../redux/requestSlice'

import {
  GuestSignup,
  MemberSignup,
} from '../models';

import {
  SIGNUP_MAX_STAY_DAYS
} from '../global';

// UTC to local date formatting
import { useDates } from '../hooks/UseDates';

interface UseSignupDatesInterface {
  calcMinArrival: (departure: string) => string;
  calcMaxDeparture: (departure: string) => string;
  calcMaxArrival: (departure: string) => string;
  calcMinDeparture: (departure: string) => string;
  calculateGuestDateRestrictions: (ms: Array<GuestSignup>) => void;
  calculateMemberDateRestrictions: (ms: Array<MemberSignup>) => void;
  getOffSeasonStartDate: () => string;
  getRequestYearEndDate: () => string;
  getRequestYearStartDate: () => string;
  isOffSeason: () => boolean;
}

export const useSignupDates = (): UseSignupDatesInterface => {

  const { dateFormatted } = useDates();

  // Redux
  const dispatch = useAppDispatch();
  const offSeasonStartDate = useAppSelector(getOffSeasonStartDateValue);

  /*
   * Calculate minimum arrival date
   */
  const calcMinArrival = (departure: string): string => {
    const d = parse(departure, 'MM/dd/yyyy', new Date());
    return dateFormatted(subDays(d, SIGNUP_MAX_STAY_DAYS));
  }

  /*
   * Calculate maximum arrival date
   */
  const calcMaxArrival = (departure: string): string => {
    const d = parse(departure, 'MM/dd/yyyy', new Date());
    return dateFormatted(subDays(d, 1));
  }

  /*
   * Calculate minimum departure date
   */
  const calcMinDeparture = (arrival: string): string => {
    const a = parse(arrival, 'MM/dd/yyyy', new Date());
    return dateFormatted(addDays(a, 1));
  }

  /*
   * Calculate maximum departure date
   */
  const calcMaxDeparture = (arrival: string): string => {
    const a = parse(arrival, 'MM/dd/yyyy', new Date());
    return dateFormatted(addDays(a, SIGNUP_MAX_STAY_DAYS));
  }

  /*
* Calculate min start and max end dates for guests
*/
  const calculateGuestDateRestrictions = (gs: Array<GuestSignup>) => {

    let min: Date | undefined;
    let max: Date | undefined;

    // Find min/max for all active and life members
    gs.forEach(gs => {
      const a = parse(gs.arrivalDate, 'MM/dd/yyyy', new Date());
      if (isDateValid(a)) {
        if (!min || a < min) {
          min = a;
        }
      }

      const d = parse(gs.departureDate, 'MM/dd/yyyy', new Date());
      if (isDateValid(d)) {
        if (!max || d > max) {
          max = d;
        }
      }
    });

    // Update Redux signup dates
    if (min) {
      dispatch(setRequestMinArrival(dateFormatted(min)));
    }

    if (max) {
      dispatch(setRequestMaxDeparture(dateFormatted(max)));
    }

  }

  /*
* Calculate min start and max end dates for members
*/
  const calculateMemberDateRestrictions = (ms: Array<MemberSignup>) => {

    let min: Date | undefined;
    let max: Date | undefined;

    // Find min/max for all active and life members
    ms.forEach(ms => {
      const a = parse(ms.arrivalDate, 'MM/dd/yyyy', new Date());
      if (isDateValid(a)) {
        if (!min || a < min) {
          min = a;
        }
      }

      const d = parse(ms.departureDate, 'MM/dd/yyyy', new Date());
      if (isDateValid(d)) {
        if (!max || d > max) {
          max = d;
        }
      }
    });

    // Update Redux signup dates
    if (min) {
      dispatch(setRequestMinArrival(dateFormatted(min)));
    }

    if (max) {
      dispatch(setRequestMaxDeparture(dateFormatted(max)));
    }

  }

  /*
 * Get the last day of the request year (11/30/yyyy)
 */
  const getRequestYearEndDate = (): string => {

    const now = new Date();
    const month = getMonth(now);
    // Dec
    if (month === 11) {
      return dateFormatted(new Date((getYear(now) + 1), 10, 30)); // 11/30 next year
    } else {
      return dateFormatted(new Date(getYear(now), 10, 30)); // 11/30 this year
    }

  }

  /*
 * Get offseason start date
 */
  const getOffSeasonStartDate = (): string => {
    return offSeasonStartDate;
  }

  /*
   * Get the first day of the fiscal year (12/1/yyyy)
   */
  const getRequestYearStartDate = (): string => {

    const now = new Date();
    const month = getMonth(now);

    // Dec
    if (month === 11) {
      return dateFormatted(new Date(getYear(now), 11, 1)); // 12/1 next year
    } else {
      return dateFormatted(new Date((getYear(now) - 1), 11, 1)); // 12/1 this year
    }

  }

  /*
  * Is the current date in the offseason
  */
  const isOffSeason = (): boolean => {

    if (offSeasonStartDate !== undefined) {
      // Set offseason flag in local state
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const offSeasonDate = new Date(offSeasonStartDate)
      return (today >= offSeasonDate);
    } else {
      return false;
    }

  }

  return {
    calcMaxArrival,
    calcMaxDeparture,
    calcMinArrival,
    calcMinDeparture,
    calculateGuestDateRestrictions,
    calculateMemberDateRestrictions,
    getOffSeasonStartDate,
    getRequestYearEndDate,
    getRequestYearStartDate,
    isOffSeason
  };
}